import { getCdnURL } from '@/domains/care/helpers';
import {
  HYDRATE,
  type HydrateAction,
} from '@/domains/core/applicationState/store/hydrateAction';
import { routes } from '@/domains/core/routing/routes/routes.default';
import { createURL } from '@/domains/core/routing/URLParser';
import { processAttributes } from '@/domains/productDiscovery/ProductPage/common/processors/processAttributes';
import { MainProductDataFragment } from '@/domains/productDiscovery/ProductPage/graphql/productPageMain/generated/productPageMain.generated';
import {
  BaseDetail,
  Breadcrumb,
} from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import {
  detailErrorSelector,
  detailSelector,
} from '@/domains/productDiscovery/ProductPage/selectors/detail';
import { CarbonScoreEquivalence } from '@/domains/sustainability/carbonScore/data/carbonScore.model';
import { getSecondHandInfo } from '@/domains/sustainability/secondHand/utils/secondHand';
import { createAttributeLabel } from '@/productDiscovery/ProductPage/common/components/VariantSelector/utils';

import type { SpartacuxProductDiscoveryDomainState } from '../productDiscovery.model';
import {
  GET_BASIC_DETAIL,
  GET_BASIC_DETAIL_SUCCESS,
  SET_BASIC_DETAIL,
  type Action as GetBasicDetailAction,
} from './basicDetail.actions';
import {
  GET_DETAIL_SUCCESS,
  GRAPHQL_SUCCESS,
  GraphqlSuccess,
  SET_DETAIL,
  type Action as GetDetailAction,
} from './detail.actions';
import { getInitialState, type DetailState } from './interfaces/Detail.model';
import type { Market } from './interfaces/Market';

export default function reducer<M extends Market>(
  state: DetailState<M> = getInitialState<M>(),
  action:
    | HydrateAction<SpartacuxProductDiscoveryDomainState>
    | GetDetailAction<M>
    | GetBasicDetailAction
    | GraphqlSuccess,
): DetailState<M> {
  switch (action.type) {
    case HYDRATE:
      return {
        ...state,
        error: detailErrorSelector(action.state),
        data: detailSelector(action.state),
      };

    case SET_DETAIL:
    case GET_DETAIL_SUCCESS:
      return {
        ...state,
        data: action.detail,
        fetching: false,
      };
    case GRAPHQL_SUCCESS:
      return {
        ...state,
        data: processDetail(action.payload.mainData),
        fetching: false,
      };

    case GET_BASIC_DETAIL:
      return {
        ...state,
        fetchingBasicDetail: true,
      };

    case SET_BASIC_DETAIL:
    case GET_BASIC_DETAIL_SUCCESS:
      return {
        ...state,
        data: state.data ? { ...state.data, ...action.basicDetail } : undefined,
        fetchingBasicDetail: false,
      };

    default:
      return state;
  }
}

const processDetail = (data: MainProductDataFragment): BaseDetail => {
  const cdnURL = getCdnURL();

  const {
    secondHandTitle: updatedTitle,
    isSecondHand,
    productCondition,
    context,
  } = getSecondHandInfo(data.sku.title || '');

  const infoDocs: BaseDetail['infoDocs'] = [];

  if (data.sku.description.informationPdfUrl) {
    infoDocs.push({
      key: 'productInformationPdf',
      url: data.sku.description.informationPdfUrl,
    });
  }
  if (data.sku.description.instructionsPdfUrl) {
    infoDocs.push({
      key: 'productInstructionsPdf',
      url: data.sku.description.instructionsPdfUrl,
    });
  }
  if (data.sku.repairability.pdfUrl) {
    infoDocs.push({
      key: 'repairabilityIndexPdf',
      url: data.sku.repairability.pdfUrl,
    });
  }

  let breadcrumbs: Breadcrumb[] = [];
  if (data.sku.merchandisingCategory) {
    breadcrumbs =
      data.sku.merchandisingCategory?.ancestors?.map((ancestor) => ({
        id: Number(ancestor.publicId),
        name: ancestor.name,
        url: `/${ancestor.slug}-${ancestor.publicId}`,
      })) || [];

    breadcrumbs.push({
      id: Number(data.sku.merchandisingCategory.publicId),
      name: data.sku.merchandisingCategory.name,
      url: `/${data.sku.merchandisingCategory.slug}-${data.sku.merchandisingCategory.publicId}`,
    });
  }

  return {
    articleId: Number(data.sku.masterProduct.articleId),
    productId: Number(data.productId),
    modelId: Number(data.sku.modelId),
    offerId: data.id,
    categoryId: Number(data.sku.merchandisingCategory?.publicId),
    productRef: data.sku.reference,
    manufacturerSKU: data.sku.manufacturer.sku,
    offerSKU: data.sku.sellerReference || '', // TODO: rename the property in detail
    isSellable: data.isSellable,
    isProProduct: data.market === 'B2B',
    isOnTopSale: data.isTopSales || false,
    isBestQualityOffer: data.isBestOffer,
    isAvailableInPro: false, // TODO no more used delete this value
    isSecondHand,
    otherSellers: {
      total: data.competingOffersCount || 0,
    },
    variantExperience: data.sku.masterProduct.variantExperience,
    totalModels: data.variantsCount,
    secondHandDetails: context
      ? {
          context,
          productCondition: productCondition || null,
        }
      : null,
    commercialOperationIds: [], // TODO remove in model
    title: updatedTitle,
    isIndexable: true, // TODO remove
    articlePath: `${data.sku.masterProduct.slug}-${data.sku.masterProduct.articleId}`,
    media: data.sku.images.map((image) => ({
      regularUrl: `${cdnURL}${image.regularUrl}`,
      largeUrl: `${cdnURL}${image.largeUrl}`,
      thumbnailUrl: `${cdnURL}${image.thumbnailUrl}`,
      type: 'image',
    })),
    breadcrumbs,
    stock: {
      minimumValue: Number(data.packaging.minimumQuantity),
      increaseQuantity: Number(data.packaging.increment),
    },
    price: {
      primaryPrice: {
        amountWithVat: data.pricing.sellPrice.amountVatIncluded,
        amountWithoutVat: data.pricing.sellPrice.amountVatExcluded,
      },
      retailPrice: data.pricing.retailPrice
        ? {
            amountWithVat: data.pricing.retailPrice.amountVatIncluded,
            amountWithoutVat: data.pricing.retailPrice.amountVatExcluded,
          }
        : null,
      measurementPrice: data.pricing.measurementSellPrice
        ? {
            amountWithVat:
              data.pricing.measurementSellPrice.price.amountVatIncluded,
            amountWithoutVat:
              data.pricing.measurementSellPrice.price.amountVatExcluded,
            measurementUnit: data.pricing.measurementSellPrice.unit || '',
          }
        : null,
      ecoParticipationWithVat:
        data.pricing.ecoParticipation?.amountVatIncluded || NaN,
      discountPercentage: data.pricing.discountPercentage,
    },
    infoDocs,
    currency: data.pricing.sellPrice.currency,
    averageRating: 0, // TODO remove in the model
    totalRating: 0, // TODO remove in the model
    carbonScore: processCarbonScore(data.sku.carbonScore),
    safetyInformationPdf: data.sku.description.safetyInformationPdfUrl || '',
    ean: data.sku.ean,
    warranty: null, // TODO remove it from the model
    description: data.sku.description.textual || '',
    descriptionFileUrl: data.sku.description.informationPdfUrl || '',
    attributes: processAttributes(
      data.sku.attributes.map((attr) => ({
        id: attr.id,
        name: attr.name,
        value: attr.values.map((v) => v.value).join(', '),
        unit: attr.unit?.abbreviation || '',
      })),
      {
        productRef: data.sku.reference,
        manufacturerSKU: data.sku.manufacturer.sku,
        ean: data.sku.ean,
      },
    ),
    variantAttributes: data.sku.attributes.map((attr) => ({
      ...attr,
      label: createAttributeLabel(attr),
    })),
    unitCount: NaN, // TODO update when we have it in the query
    seller: {
      name: data.seller.contractName || '',
      url: createURL(routes.seller, {
        id: data.seller.contractId,
      }),
      country: data.seller.countryLocation || '',
      id: Number(data.seller.contractId),
    },
    commercialAnimation: data.commercialAnimation || null,
    delivery: {
      // weight is in KG
      weight: data.weightInGrams ? data.weightInGrams / 1000 : 0,
      fulfillment: data.seller.isMF || false,
    },
    // TODO: remove ASAP
    models:
      data.sku.masterProduct.variantExperience == 'LEGACY'
        ? [
            {
              modelId: Number(data.sku.modelId),
              description: data.sku.title || '',
              isProductB2b: data.market == 'B2B',
            },
          ]
        : [],
  };
};

const processCarbonScore = (
  carbonScore: MainProductDataFragment['sku']['carbonScore'],
): BaseDetail['carbonScore'] => {
  if (!carbonScore) {
    return null;
  }

  return {
    calculationMethodology: carbonScore.calculationMethodology,
    externalCategory: {
      averageCarbonFootprint:
        carbonScore.externalCategoryCarbonScore.averageCarbonFootprint,
      averageCarbonScore:
        CarbonScoreEquivalence[
          carbonScore.externalCategoryCarbonScore.averageRating
        ],
      averagePosition: Number(
        carbonScore.externalCategoryCarbonScore.averagePosition,
      ),
      maxCarbonFootprint:
        carbonScore.externalCategoryCarbonScore.maximumCarbonFootprint,
      minCarbonFootprint:
        carbonScore.externalCategoryCarbonScore.maximumCarbonFootprint,
      totalProductsCount: Number(
        carbonScore.externalCategoryCarbonScore.productCount,
      ),
    },
    product: {
      carbonFootprint: carbonScore.skuCarbonScore.carbonFootprint,
      carbonScore: CarbonScoreEquivalence[carbonScore.skuCarbonScore.rating],
      confidenceIndicator: carbonScore.skuCarbonScore.confidenceIndicator,
      lifecycleAssessment: {
        endOfLifeCarbonFootprint:
          carbonScore.skuCarbonScore.lifecycleAssessment
            .endOfLifeCarbonFootprint,
        manufacturingCarbonFootprint:
          carbonScore.skuCarbonScore.lifecycleAssessment
            .manufacturingCarbonFootprint,
        materialExtractionCarbonFootprint:
          carbonScore.skuCarbonScore.lifecycleAssessment
            .materialExtractionCarbonFootprint,
        transportationCarbonFootprint:
          carbonScore.skuCarbonScore.lifecycleAssessment
            .transportationCarbonFootprint,
        usageCarbonFootprint:
          carbonScore.skuCarbonScore.lifecycleAssessment.usageCarbonFootprint,
      },
      positionInCategory: Number(carbonScore.skuCarbonScore.positionInCategory),
    },
  };
};
